import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { Chip } from "shared/ui/chip/Chip"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamInvitation } from "entities/team-invitations/types/TeamInvitation"
import { ProjectTeamInvitationCardLoader } from "features/project-team/ui/project-team-invitation-card/ui/ProjectTeamInvitationCardLoader"

import { ProjectTeamInvitationCard } from "../../project-team-invitation-card/ProjectTeamInvitationCard"
import { ProjectTeamInvitationListProps } from "../ProjectTeamInvitationList"
import "../styles/_project-team-invitation-list.scss"

export interface ProjectTeamInvitationListViewModel
  extends ProjectTeamInvitationListProps {
  teamInvitations: TeamInvitation[]
  hasInvitations: boolean
  pendingInvitationCount: number
}

interface ProjectTeamInvitationListViewProps
  extends ProjectTeamInvitationListViewModel,
    WithClassName {}

export const ProjectTeamInvitationListView = React.memo(
  ({
    teamInvitations,
    onReject,
    onAccept,
    onNavigateToProject,
    pendingInvitationCount,
    hasInvitations,
    className,
    isLoading = false,
    loadingItemCount = 3
  }: ProjectTeamInvitationListViewProps) => {
    const { t } = useTranslation()

    return (
      <div className={classNames("project-team-invitation-list", className)}>
        <div className="project-team-invitation-list__header">
          <h3 className="project-team-invitation-list__title">
            <Icon name={IconName.TEAM} /> {t("Team.Invitation.Invitations")}
          </h3>
          {pendingInvitationCount > 0 ? (
            <Chip className="chip--small project-team-invitation-list__count">
              {pendingInvitationCount}
            </Chip>
          ) : null}
        </div>
        <div className="project-team-invitation-list__description">
          {t("Team.Invitation.List.Description")}
        </div>

        {isLoading && !hasInvitations ? (
          <div className="project-team-invitation-list__items">
            {Array.from({ length: loadingItemCount }).map((_, index) => (
              <ProjectTeamInvitationCardLoader
                // eslint-disable-next-line react/no-array-index-key
                key={`project-team-invitation-list-loader-${index}`}
                className="project-team-invitation-list__item"
              />
            ))}
          </div>
        ) : null}

        {hasInvitations ? (
          <ul className="project-team-invitation-list__items">
            {teamInvitations.map(teamInvitation => (
              <ProjectTeamInvitationCard
                key={teamInvitation.id}
                teamInvitation={teamInvitation}
                onReject={onReject}
                onAccept={onAccept}
                onNavigateToProject={onNavigateToProject}
                className="project-team-invitation-list__item"
              />
            ))}
          </ul>
        ) : null}

        {!hasInvitations && !isLoading ? (
          <div className="project-team-invitation-list__empty-note">
            {t("Team.Invitation.List.Empty")}
          </div>
        ) : null}
      </div>
    )
  }
)
